import { ReactElement, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomInvestigationModel,
  EditCustomInvestigationModel,
  useEditInvestigationMutation,
} from '@customer/store/apis/investigationsApi';
import { DateTime } from 'luxon';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { AssetBasicModel } from '@customer/store/apis/assetsApi';
import {
  CustomInvestigationForm,
  CustomInvestigationInputFields,
  CustomInvestigationOutputFields,
} from '../../../custom-investigation-form/CustomInvestigationForm';
import { getCreateCustomInvestigationArgs } from '../../../create-custom-investigation-panel/components/form/CreateCustomInvestigationForm';
import { ModelName } from '@customer/store/types';
import { SuccessNotificationContext } from '@dmc/ui';

type InputFields = CustomInvestigationInputFields;
type OutputFields = CustomInvestigationOutputFields;
type MutationArgs = EditCustomInvestigationModel;

export interface CustomInvestigationEditSubset
  extends Pick<
    CustomInvestigationModel,
    'id' | 'it' | 'name' | 'tsStart' | 'tsEnd' | 'mode' | 'oneSecLog' | 'assets'
  > {}

interface EditCustomInvestigationProps {
  assets: AssetBasicModel[];
  investigation: CustomInvestigationEditSubset;
}

export const EditCustomInvestigationForm = ({
  assets,
  investigation,
}: EditCustomInvestigationProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);
  const [triggerMutation] = useEditInvestigationMutation();
  const timezone = useAppSelector(state => state.auth.siteTimezone);

  const defaultValues = useMemo<InputFields>(() => {
    const { name, tsStart, tsEnd, mode, oneSecLog, assets: existingAssets } = investigation;

    // Use a sanitised list of the investigation's assets.
    const existingAssetIds = existingAssets.map(a => a.asset); // 'asset' is the asset id.
    const existingAndSelectableAssetIds = existingAssetIds.filter(id =>
      assets.some(asset => asset.id === id),
    );

    let duration = 0;
    let startDate: string | null = null;
    let startTime: string | null = null;

    const startDateTime = DateTime.fromISO(tsStart, { zone: timezone });
    const endDateTime = DateTime.fromISO(tsEnd, { zone: timezone });
    const { minutes } = endDateTime.diff(startDateTime, ['minutes']).toObject();

    // Check the date time strings are loosely valid before using them.
    if (startDateTime.isValid && endDateTime.isValid && typeof minutes === 'number') {
      duration = minutes;
      startTime = startDateTime.toString();
      startDate = startDateTime.startOf('day').toString();
    }

    return {
      name,
      analysisMode: mode,
      oneSecLog,
      assetIds: existingAndSelectableAssetIds,
      startDate,
      startTime,
      duration,
    } satisfies InputFields;
  }, [investigation, timezone, assets]);

  const getEditArgs = useCallback(
    (fieldValues: OutputFields): MutationArgs => {
      const body = getCreateCustomInvestigationArgs(fieldValues);
      return { ...body, investigationId: investigation.id.toString() };
    },
    [investigation.id],
  );

  const successCallback = useCallback(() => {
    showSuccessMessage(t('Model.Edit.Success', { modelName: ModelName.Investigation }));
    dispatch(closeSidePanel());
  }, [dispatch, showSuccessMessage, t]);

  return (
    <CustomInvestigationForm
      defaultValues={defaultValues}
      getArgs={getEditArgs}
      triggerMutation={triggerMutation}
      successCallback={successCallback}
      assets={assets}
      validateOnLoad={true}
      sliceKey={`AssetTracksForm--${investigation.id}`}
      submitLabel={t('SaveChanges')}
    />
  );
};
